/**
 * Smart Bill Import (V2), phase 1: a bill document arriving in the app —
 * from the share sheet on a phone, a drop or the file picker on the web.
 * It is only held in memory while it is read: never stored, never sent
 * anywhere. Importing is an alternative way in; manual entry is untouched.
 */
export type DocumentKind = 'pdf' | 'jpeg' | 'png' | 'webp' | 'heic';
export type DocumentSource = 'picker' | 'drop' | 'share';

export interface ImportedDocument {
  name: string;
  kind: DocumentKind;
  /** The document's own bytes. */
  bytes: Uint8Array;
  source: DocumentSource;
}

/** Bills are a few pages; anything past this is not a bill (and would stall a phone). */
export const MAX_DOCUMENT_BYTES = 20 * 1024 * 1024;

export const MIME: Record<DocumentKind, string> = {
  pdf: 'application/pdf',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  heic: 'image/heic',
};

/** What the file picker and the share sheet offer. */
export const ACCEPT = 'application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif,.pdf,.jpg,.jpeg,.png,.webp,.heic,.heif';

const ascii = (b: Uint8Array, from: number, to: number) => String.fromCharCode(...b.subarray(from, to));

/**
 * What a file really is, from its first bytes (a name or a MIME type can
 * lie: "bill.pdf" may be a photo, a shared file may come without a type).
 */
export function sniff(bytes: Uint8Array): DocumentKind | null {
  if (bytes.length < 12) return null;
  // A PDF may have a few bytes of junk before its header; the spec allows it within the first 1 KB.
  const head = ascii(bytes, 0, Math.min(bytes.length, 1024));
  if (head.includes('%PDF-')) return 'pdf';
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'jpeg';
  if (bytes[0] === 0x89 && ascii(bytes, 1, 4) === 'PNG') return 'png';
  if (ascii(bytes, 0, 4) === 'RIFF' && ascii(bytes, 8, 12) === 'WEBP') return 'webp';
  if (ascii(bytes, 4, 8) === 'ftyp' && /^(heic|heix|hevc|heim|heis|mif1|msf1)$/.test(ascii(bytes, 8, 12))) return 'heic';
  return null;
}

export type AcceptResult = { ok: true; document: ImportedDocument } | { ok: false; reason: 'empty' | 'tooLarge' | 'unsupported' | 'unreadable' };

export function acceptDocument(name: string, bytes: Uint8Array, source: DocumentSource): AcceptResult {
  if (bytes.length === 0) return { ok: false, reason: 'empty' };
  if (bytes.length > MAX_DOCUMENT_BYTES) return { ok: false, reason: 'tooLarge' };
  const kind = sniff(bytes);
  if (!kind) return { ok: false, reason: 'unsupported' };
  return { ok: true, document: { name: name.trim() || 'bill', kind, bytes, source } };
}

export async function readFile(file: File, source: DocumentSource): Promise<AcceptResult> {
  if (file.size > MAX_DOCUMENT_BYTES) return { ok: false, reason: 'tooLarge' };
  return acceptDocument(file.name, new Uint8Array(await file.arrayBuffer()), source);
}

/* ------------------------------------------------------------ hand-over */

/**
 * The one document on its way to the import screen. The share sheet can
 * deliver before the screen exists (a cold start), so it waits here.
 */
let pending: AcceptResult | null = null;
const listeners = new Set<() => void>();

export function offerDocument(result: AcceptResult) {
  pending = result;
  listeners.forEach((l) => l());
}

export function takeDocument(): AcceptResult | null {
  const r = pending;
  pending = null;
  return r;
}

export function onDocumentOffered(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function formatSize(bytes: number, locale: string): string {
  const kb = bytes / 1024;
  return kb < 1024
    ? `${new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Math.max(1, kb))} KB`
    : `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(kb / 1024)} MB`;
}
